export type DiffKind = 'same' | 'added' | 'removed';

export interface DiffPart {
  kind: DiffKind;
  text: string;
}

/**
 * Word-level diff used to show what the AI changed before the author accepts it.
 *
 * Longest-common-subsequence over words: the texts here are a paragraph or two,
 * so the quadratic table is far cheaper than pulling in a diff library.
 */
export function diffWords(before: string, after: string): DiffParts {
  const a = split(before);
  const b = split(after);

  const lengths = lcsTable(a, b);
  const parts: DiffPart[] = [];

  let i = 0;
  let j = 0;

  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      push(parts, 'same', a[i]);
      i++;
      j++;
    } else if (lengths[i + 1][j] >= lengths[i][j + 1]) {
      push(parts, 'removed', a[i]);
      i++;
    } else {
      push(parts, 'added', b[j]);
      j++;
    }
  }

  while (i < a.length) push(parts, 'removed', a[i++]);
  while (j < b.length) push(parts, 'added', b[j++]);

  return parts;
}

type DiffParts = DiffPart[];

/** Keeps whitespace attached so re-joining the parts reproduces the text exactly. */
function split(text: string): string[] {
  return text.length === 0 ? [] : (text.match(/\s+|[^\s]+/g) ?? []);
}

function push(parts: DiffPart[], kind: DiffKind, text: string): void {
  const last = parts[parts.length - 1];
  if (last?.kind === kind) {
    last.text += text;
    return;
  }
  parts.push({ kind, text });
}

function lcsTable(a: string[], b: string[]): number[][] {
  const table: number[][] = Array.from({ length: a.length + 1 }, () => new Array<number>(b.length + 1).fill(0));

  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      table[i][j] = a[i] === b[j] ? table[i + 1][j + 1] + 1 : Math.max(table[i + 1][j], table[i][j + 1]);
    }
  }

  return table;
}
